import { UiText } from '@experiences/ui-common';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Autocomplete from '@mui/material/Autocomplete';
import type { InputProps } from '@mui/material/Input';
import ListItem from '@mui/material/ListItem';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import { makeStyles } from '@mui/styles';
import Tokens, { FontVariantToken } from '@uipath/apollo-core';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useSWR from 'swr';
import { useDebounce } from 'use-debounce';

import { getSlackChannels } from '../../../../services/notification-preferences';
import { accountGlobalId } from '../../../../store/selectors';
import { useTenantsContext } from '../../../tenants/TenantsContextProvider';
import type { ISlackChannel } from '../../interfaces/notificationSettings';

const useStyles = makeStyles((theme) => ({
    selectinput: {
        width: '100%',
        marginTop: '10px',
    },
    text: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
    },
    inputLabel: {
        fontWeight: Tokens.FontFamily.FontBrandH4Weight,
        fontSize: Tokens.FontFamily.FontMSize,
        color: theme.palette.semantic.colorForegroundDeEmp,
        float: 'left',
    },
    infoIcon: {
        marginLeft: Tokens.Padding.PadM,
        height: Tokens.FontFamily.FontXsLineHeight,
        width: Tokens.Padding.PadXxxl,
        marginTop: '3px',
    },
    helptextContainer: {
        marginTop: Tokens.Padding.PadM,
        fontSize: Tokens.FontFamily.FontSSize,
        fontWeight: Tokens.FontFamily.FontWeightDefault,
        color: theme.palette.semantic.colorForegroundDeEmp,
    },
}));

interface IUiSlackChannelPicker {
    value: ISlackChannel;
    connectionId: string;
    searchOnly?: boolean;
    inputPlaceholder?: string;
    InputProps?: InputProps;
    form?: boolean;
    onChange?: (values: any[]) => void;
    clearErrors?: () => void;
    labelText: string;
    showTooltip: boolean;
    helpText?: string;
}

export const UiSlackChannelPicker = ({
    value,
    searchOnly,
    onChange,
    clearErrors,
    connectionId = '',
    labelText,
    showTooltip = true,
    helpText = '',
}: IUiSlackChannelPicker) => {
    const classes = useStyles();
    const { formatMessage: translate } = useIntl();

    const [ query, setQuery ] = useState<string>(value.name ?? '');
    const [ debouncedQuery ] = useDebounce(query, 500);
    const [ selected, setSelected ] = useState<ISlackChannel[]>([ value ]);

    const [ inputValue, setInputValue ] = useState<string | undefined>(undefined);
    const accountId = useSelector(accountGlobalId);
    const { selectedTenant: { id: tenantId } } = useTenantsContext();

    const {
        data: options, isValidating: loading,
    } = useSWR(
        connectionId && debouncedQuery.trim() ? {
            SupplementConnectionId: connectionId,
            startsWith: debouncedQuery,
            selectedTenantId: tenantId,
            selectedAccountId: accountId,
        } : null,
        getSlackChannels,
    );

    return (
        <Autocomplete
            className={classes.selectinput}
            data-cy="slack-channel-picker"
            getOptionLabel={(option) =>
                typeof option === 'string' ? option : option.name}
            filterOptions={(x) => x}
            options={options ?? []}
            autoComplete
            includeInputInList
            inputValue={inputValue ?? value?.name ?? ''}
            filterSelectedOptions
            value={searchOnly ? selected[0] : (([ value ]) as any[])}
            noOptionsText={query ? translate({ id: 'CLIENT_SLACK_CHANNEL_PICKER_NO_OPTIONS' }, { query }) : ''}
            loading={!!loading}
            loadingText={translate({ id: 'CLIENT_SLACK_CHANNEL_PICKER_LOADING' })}
            onChange={(_event, newValue, reason) => {
                onChange?.([ newValue ]);

                if (reason === 'removeOption' || reason === 'clear') {
                    clearErrors?.();
                }

                if (Array.isArray(newValue)) {
                    setSelected(newValue);
                } else {
                    setSelected([ newValue ]);
                }

            }}
            onInputChange={(event, newValue) => {
                if (!event) {
                    return;
                }
                setInputValue(newValue);
                setQuery(newValue.trim());
            }}
            renderInput={(params) => (
                <>
                    <TextField
                        {...params}
                        label={<>
                            <UiText
                                className={classes.inputLabel}>
                                {labelText}
                            </UiText>
                            {showTooltip && <Tooltip
                                arrow
                                title={translate({ id: 'CLIENT_NOTIFICATION_SLACKCONFIG_CHANNEL_TOOLTIP' })}>
                                <InfoOutlined
                                    className={classes.infoIcon}
                                    tabIndex={0}
                                    aria-label={translate({ id: 'CLIENT_NOTIFICATION_SLACKCONFIG_CHANNEL_TOOLTIP' })}
                                />
                            </Tooltip>}
                        </>}
                        fullWidth />
                    {helpText && <UiText
                        className={classes.helptextContainer}
                        variant={FontVariantToken.fontSizeS} >
                        {helpText}
                    </UiText> }
                </>
            )}
            renderOption={(props, option: ISlackChannel) => (
                <ListItem
                    {...props}
                    divider
                    disableGutters>
                    <div className={classes.text}>
                        <UiText>
                            {option.name}
                        </UiText>
                    </div>
                </ListItem>
            )}
        />

    );
};
